import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Box,
  BoxProps as MuiBoxProps,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  SelectChangeEvent,
} from '@mui/material'

// Icons
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import CategoryIcon from '@mui/icons-material/Category'
import UploadIcon from '@mui/icons-material/Upload'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import RefreshIcon from '@mui/icons-material/Refresh'

import { useAppSelector, useAppDispatch } from '../hooks'
import {
  setCategories,
  addCategory,
  updateCategory,
  deleteCategory,
  setEntities,
  addEntity,
  updateEntity,
  deleteEntity,
} from '../stores/EntityCatalogueStore'
import { EntityCategory, Entity } from '../types/EntityCatalogue'

const Container = styled.div`
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
  background: var(--sevv-bg-paper, #ffffff);
  border-radius: 1rem;
  border: 1px solid var(--sevv-divider, #eeeeee);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
`

const AdminHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`

const AdminTitle = styled(Typography)`
  font-weight: 600;
  font-size: 1.5rem;
  line-height: 1.25;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--sevv-text-primary, #424242);
  font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  letter-spacing: -0.025em;

  svg {
    color: var(--sevv-primary, #f97415);
    font-size: 1.75rem;
    filter: drop-shadow(0 0 8px color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent));
  }
`

const StyledTabs = styled(Tabs)`
  margin-bottom: 2rem;
  background: var(--sevv-bg-paper, #ffffff);
  border: 1px solid var(--sevv-divider, #eeeeee);
  border-bottom: none;
  border-radius: 0.75rem 0.75rem 0 0;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);

  .MuiTabs-indicator {
    background-color: var(--sevv-primary, #f97415);
    height: 3px;
    border-radius: 3px 3px 0 0;
  }

  .MuiTabs-flexContainer {
    gap: 0;
  }

  .MuiTabs-scrollButtons {
    color: var(--sevv-text-secondary, #757575);
    transition: all 0.2s ease;
    
    &:hover {
      color: var(--sevv-primary, #f97415);
      background-color: color-mix(in srgb, var(--sevv-primary, #f97415) 5%, transparent);
    }
    
    &.Mui-disabled {
      color: var(--sevv-gray-200, #eeeeee);
    }
  }

  .MuiTab-root {
    text-transform: none;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5;
    transition: all 0.2s ease;
    min-height: 3rem;
    padding: 0.75rem 1.5rem;
    color: var(--sevv-text-secondary, #757575);
    font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    white-space: nowrap;
    border-radius: 0;

    &:hover {
      background-color: color-mix(in srgb, var(--sevv-primary, #f97415) 5%, transparent);
      color: var(--sevv-primary, #f97415);
    }

    &.Mui-selected {
      color: var(--sevv-primary, #f97415);
      font-weight: 600;
      background-color: transparent;
    }
  }
`

const StyledCard = styled(Card)`
  margin-bottom: 2rem;
  background: var(--sevv-bg-paper, #ffffff) !important;
  border-radius: 1rem !important;
  border: 1px solid var(--sevv-divider, #eeeeee);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  overflow: visible !important;

  .MuiCardContent-root {
    padding: 1.75rem;
  }
`

const EntityGrid = styled(Grid)`
  margin-top: 1rem;
  padding: 0.25rem 0.5rem 1rem 0.5rem;
`

const EntityItem = styled(Box)`
  position: relative;
  background: var(--sevv-bg-paper, #ffffff);
  border-radius: 0.75rem;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid var(--sevv-gray-100, #f5f5f5);
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-0.25rem);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    border-color: var(--sevv-primary, #f97415);

    .entity-actions {
      opacity: 1;
    }
  }
`

// Create a wrapper component that doesn't pass imageUrl to DOM
const EntityImageWrapper = ({ imageUrl, ...rest }: { imageUrl: string } & MuiBoxProps) => {
  return <Box {...rest} sx={{ backgroundImage: `url(${imageUrl})` }} />
}

// Style the wrapper component
const EntityImage = styled(EntityImageWrapper)`
  height: 11.25rem;
  background-size: cover;
  background-position: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  }
`

const CategoryBadge = styled(Chip)`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent);
  color: var(--sevv-primary, #f97415);
  font-weight: 600;
  font-size: 0.75rem;
  border: 1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent);
  backdrop-filter: blur(4px);
  z-index: 1;
  font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  border-radius: 0.5rem;
`

const EntityActions = styled(Box)`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 1;

  .MuiIconButton-root {
    backdrop-filter: blur(4px);
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }
`

const EntityInfo = styled(Box)`
  padding: 1.125rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`

const EntityName = styled(Typography)`
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.25;
  margin-bottom: 0.5rem;
  color: var(--sevv-text-primary, #424242);
  font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  letter-spacing: -0.01em;
`

const EntityDescription = styled(Typography)`
  color: var(--sevv-text-secondary, #757575);
  font-size: 0.875rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
`

const NoEntitiesMessage = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--sevv-bg-default, #fafafa);
  border-radius: 0.75rem;
  padding: 3rem 1.5rem;
  margin: 1rem 0;
  border: 1px dashed var(--sevv-gray-200, #eeeeee);
  text-align: center;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;

  .MuiFormControl-root {
    margin-bottom: 0;
  }

  .MuiInputBase-root {
    background: var(--sevv-bg-paper, #ffffff);
    font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .MuiInputLabel-root.Mui-focused {
    color: var(--sevv-primary, #f97415);
  }

  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: var(--sevv-primary, #f97415);
  }
`

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: var(--sevv-bg-paper, #ffffff);
    border-radius: 1rem;
    border: 1px solid var(--sevv-divider, #eeeeee);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);

    .MuiDialogTitle-root {
      background: var(--sevv-bg-default, #fafafa);
      padding: 1rem 1.5rem;
      font-weight: 600;
      font-size: 1.125rem;
      line-height: 1.25;
      border-bottom: 1px solid var(--sevv-divider, #eeeeee);
      color: var(--sevv-text-primary, #424242);
      font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
      letter-spacing: -0.01em;
    }

    .MuiDialogContent-root {
      padding: 1.5rem;
    }

    .MuiDialogActions-root {
      padding: 1rem 1.5rem;
      background: var(--sevv-bg-default, #fafafa);
      border-top: 1px solid var(--sevv-divider, #eeeeee);
    }
  }
`

const ActionButton = styled(Button)`
  text-transform: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 0.5rem 1rem;
  min-height: 2.75rem;
  transition: all 0.2s ease;
  font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;

  &.MuiButton-contained {
    background: linear-gradient(90deg, var(--sevv-primary, #f97415), #fb923c);
    box-shadow: 0 4px 12px color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent);

    &:hover {
      background: linear-gradient(90deg, var(--sevv-primary-dark, #ea580c), #f97316);
      box-shadow: 0 6px 16px color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent);
      transform: translateY(-0.125rem);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.MuiButton-outlined {
    border-color: color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent);
    color: var(--sevv-primary, #f97415);

    &:hover {
      background: color-mix(in srgb, var(--sevv-primary, #f97415) 5%, transparent);
      border-color: color-mix(in srgb, var(--sevv-primary, #f97415) 50%, transparent);
    }
  }
`

const CardActionArea = styled(Box)`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.75rem;
`

const StyledTableContainer = styled(TableContainer)`
  background: var(--sevv-bg-paper, #ffffff) !important;
  border-radius: 0.75rem !important;
  border: 1px solid var(--sevv-divider, #eeeeee);
  overflow: hidden;
  margin: 0.5rem 0.25rem;

  .MuiTable-root {
    background: transparent;
  }

  .MuiTableHead-root {
    background: var(--sevv-bg-default, #fafafa);

    .MuiTableCell-root {
      color: var(--sevv-text-primary, #424242);
      font-weight: 600;
      font-size: 0.875rem;
      line-height: 1.5;
      padding: 1.125rem 1.25rem;
      border-bottom: 1px solid var(--sevv-divider, #eeeeee);
      font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    }
  }

  .MuiTableBody-root {
    .MuiTableRow-root {
      transition: background 0.2s ease;

      &:hover {
        background: color-mix(in srgb, var(--sevv-primary, #f97415) 5%, transparent);
      }
    }

    .MuiTableCell-root {
      color: var(--sevv-text-secondary, #757575);
      font-size: 0.875rem;
      line-height: 1.5;
      border-bottom: 1px solid var(--sevv-gray-100, #f5f5f5);
      padding: 1.125rem 1.25rem;
      font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    }
  }

  .MuiIconButton-root {
    color: var(--sevv-text-secondary, #757575);
    transition: all 0.2s ease;

    &:hover {
      color: var(--sevv-primary, #f97415);
      background: color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent);
    }
  }
`

// Tabs interface
interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`entity-catalogue-tabpanel-${index}`}
      aria-labelledby={`entity-catalogue-tab-${index}`}
      style={{ padding: '0 1rem 1rem' }}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `entity-catalogue-tab-${index}`,
    'aria-controls': `entity-catalogue-tabpanel-${index}`,
  }
}

const placeholderImage =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iIzIwMjgzZCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMThweCIgZmlsbD0iI2ZmZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+'

const EntityCatalogueAdminPanel: React.FC = () => {
  const dispatch = useAppDispatch()
  const categories = useAppSelector((state) => state.entityCatalogue.categories)
  const entities = useAppSelector((state) => state.entityCatalogue.entities)

  // UI state
  const [tabValue, setTabValue] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [entityDialogOpen, setEntityDialogOpen] = useState(false)
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{
    id: string
    type: 'category' | 'entity'
  } | null>(null)

  // Form state
  const [editMode, setEditMode] = useState(false)
  const [currentCategoryId, setCurrentCategoryId] = useState<string | null>(null)
  const [currentEntityId, setCurrentEntityId] = useState<string | null>(null)

  // Category form
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: '',
    order: 0,
  })

  // Entity form
  const [entityForm, setEntityForm] = useState({
    name: '',
    description: '',
    imageUrl: '',
    previewImageUrl: '',
    categoryId: '',
    width: 1,
    height: 1,
    collisionEnabled: true,
    interactionEnabled: false,
    customProperties: {},
  })

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  // Open category dialog
  const handleOpenCategoryDialog = (category?: EntityCategory) => {
    setEditMode(!!category)

    if (category) {
      setCurrentCategoryId(category.id)
      setCategoryForm({
        name: category.name,
        description: category.description,
        icon: category.icon,
        order: category.order,
      })
    } else {
      setCurrentCategoryId(null)
      setCategoryForm({
        name: '',
        description: '',
        icon: 'category',
        order: categories.length,
      })
    }

    setCategoryDialogOpen(true)
  }

  // Open entity dialog
  const handleOpenEntityDialog = (entity?: Entity) => {
    setEditMode(!!entity)

    if (entity) {
      setCurrentEntityId(entity.id)
      setEntityForm({
        name: entity.name,
        description: entity.description,
        imageUrl: entity.imageUrl,
        previewImageUrl: entity.previewImageUrl,
        categoryId: entity.categoryId,
        width: entity.properties.width,
        height: entity.properties.height,
        collisionEnabled: entity.properties.collisionEnabled,
        interactionEnabled: entity.properties.interactionEnabled,
        customProperties: entity.properties.customProperties,
      })
    } else {
      setCurrentEntityId(null)
      setEntityForm({
        name: '',
        description: '',
        imageUrl: '',
        previewImageUrl: '',
        categoryId: categories.length > 0 ? categories[0].id : '',
        width: 1,
        height: 1,
        collisionEnabled: true,
        interactionEnabled: false,
        customProperties: {},
      })
    }

    setEntityDialogOpen(true)
  }

  // Close dialogs
  const handleCloseDialogs = () => {
    setCategoryDialogOpen(false)
    setEntityDialogOpen(false)
    setConfirmDeleteDialogOpen(false)
    setItemToDelete(null)
  }

  // Handle category form change
  const handleCategoryFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCategoryForm({
      ...categoryForm,
      [e.target.name]: e.target.value,
    })
  }

  // Handle entity form change
  const handleEntityFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEntityForm({
      ...entityForm,
      [e.target.name]: e.target.value,
    })
  }

  // Handle entity form select change
  const handleEntitySelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target
    if (name) {
      setEntityForm({
        ...entityForm,
        [name]: value,
      })
    }
  }

  // Handle entity form switch change
  const handleEntitySwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEntityForm({
      ...entityForm,
      [e.target.name]: e.target.checked,
    })
  }

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (id: string, type: 'category' | 'entity') => {
    setItemToDelete({ id, type })
    setConfirmDeleteDialogOpen(true)
  }

  // Confirm delete
  const handleConfirmDelete = () => {
    if (!itemToDelete) return

    if (itemToDelete.type === 'category') {
      dispatch(deleteCategory(itemToDelete.id))
    } else {
      dispatch(deleteEntity(itemToDelete.id))
    }

    setConfirmDeleteDialogOpen(false)
    setItemToDelete(null)
  }

  // Save category
  const handleSaveCategory = (e: React.FormEvent) => {
    e.preventDefault()

    if (editMode && currentCategoryId) {
      // Update existing category
      dispatch(
        updateCategory({
          id: currentCategoryId,
          name: categoryForm.name,
          description: categoryForm.description,
          icon: categoryForm.icon,
          order: categoryForm.order,
        })
      )
    } else {
      // Create new category
      dispatch(
        addCategory({
          id: `category_${Date.now()}`,
          name: categoryForm.name,
          description: categoryForm.description,
          icon: categoryForm.icon,
          order: categoryForm.order,
        })
      )
    }

    setCategoryDialogOpen(false)
  }

  // Save entity
  const handleSaveEntity = (e: React.FormEvent) => {
    e.preventDefault()

    const entityData = {
      name: entityForm.name,
      description: entityForm.description,
      imageUrl: entityForm.imageUrl || placeholderImage,
      previewImageUrl: entityForm.previewImageUrl || entityForm.imageUrl || placeholderImage,
      categoryId: entityForm.categoryId,
      properties: {
        width: entityForm.width,
        height: entityForm.height,
        collisionEnabled: entityForm.collisionEnabled,
        interactionEnabled: entityForm.interactionEnabled,
        customProperties: entityForm.customProperties,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'admin',
    }

    if (editMode && currentEntityId) {
      // Update existing entity
      dispatch(
        updateEntity({
          id: currentEntityId,
          ...entityData,
        })
      )
    } else {
      // Create new entity
      dispatch(
        addEntity({
          id: `entity_${Date.now()}`,
          ...entityData,
        })
      )
    }

    setEntityDialogOpen(false)
  }

  // Load sample data for demonstration
  const loadSampleData = () => {
    setIsLoading(true)

    // Sample categories
    const sampleCategories = [
      {
        id: 'cat_furniture',
        name: 'Furniture',
        description: 'Tables, chairs, and other furniture items',
        icon: 'chair',
        order: 0,
      },
      {
        id: 'cat_decoration',
        name: 'Decoration',
        description: 'Decorative items for your space',
        icon: 'star',
        order: 1,
      },
      {
        id: 'cat_plants',
        name: 'Plants',
        description: 'Indoor and outdoor plants',
        icon: 'spa',
        order: 2,
      },
    ]

    // Sample entities
    const sampleEntities = [
      {
        id: 'entity_chair',
        name: 'Office Chair',
        description: 'A comfortable office chair',
        imageUrl: placeholderImage,
        previewImageUrl: placeholderImage,
        categoryId: 'cat_furniture',
        properties: {
          width: 1,
          height: 1,
          collisionEnabled: true,
          interactionEnabled: true,
          customProperties: {
            sittable: true,
          },
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'admin',
      },
      {
        id: 'entity_desk',
        name: 'Desk',
        description: 'A sturdy desk for your office',
        imageUrl: placeholderImage,
        previewImageUrl: placeholderImage,
        categoryId: 'cat_furniture',
        properties: {
          width: 2,
          height: 1,
          collisionEnabled: true,
          interactionEnabled: false,
          customProperties: {},
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'admin',
      },
      {
        id: 'entity_plant',
        name: 'Potted Plant',
        description: 'A small decorative plant',
        imageUrl: placeholderImage,
        previewImageUrl: placeholderImage,
        categoryId: 'cat_plants',
        properties: {
          width: 1,
          height: 1,
          collisionEnabled: true,
          interactionEnabled: false,
          customProperties: {},
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'admin',
      },
    ]

    // Dispatch to Redux store
    dispatch(setCategories(sampleCategories))
    dispatch(setEntities(sampleEntities))

    setIsLoading(false)
  }

  // Get category name by ID
  const getCategoryName = (categoryId: string) => {
    return categories.find((cat) => cat.id === categoryId)?.name || 'Uncategorized'
  }

  // Initial load sample data if none exists
  useEffect(() => {
    if (categories.length === 0 && entities.length === 0) {
      loadSampleData()
    }
  }, [])

  return (
    <Container>
      <AdminHeader>
        <AdminTitle variant="h5">
          <CategoryIcon sx={{ color: 'var(--sevv-primary, #f97415)' }} />
          Entity Catalogue Management
        </AdminTitle>

        <Box>
          <ActionButton
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={loadSampleData}
            sx={{ marginRight: 2 }}
          >
            Load Sample Data
          </ActionButton>
        </Box>
      </AdminHeader>

      {error && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          {error}
        </Alert>
      )}

      <StyledCard>
        <StyledTabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="inherit"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Entities" {...a11yProps(0)} />
          <Tab label="Categories" {...a11yProps(1)} />
        </StyledTabs>

        <TabPanel value={tabValue} index={0}>
          <CardActionArea>
            <ActionButton
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenEntityDialog()}
            >
              Add New Entity
            </ActionButton>
          </CardActionArea>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress color="secondary" />
            </Box>
          ) : entities.length > 0 ? (
            <EntityGrid container spacing={3}>
              {entities.map((entity) => (
                <Grid item xs={12} sm={6} md={4} key={entity.id}>
                  <EntityItem>
                    <EntityImage imageUrl={entity.imageUrl || placeholderImage} />

                    <CategoryBadge label={getCategoryName(entity.categoryId)} size="small" />

                    <EntityActions className="entity-actions">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          sx={{ background: 'rgba(0,0,0,0.5)', color: 'white' }}
                          onClick={() => handleOpenEntityDialog(entity)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          sx={{ background: 'rgba(0,0,0,0.5)', color: 'white' }}
                          onClick={() => handleOpenDeleteDialog(entity.id, 'entity')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </EntityActions>

                    <EntityInfo>
                      <EntityName variant="subtitle1">{entity.name}</EntityName>
                      <EntityDescription variant="body2">{entity.description}</EntityDescription>
                    </EntityInfo>
                  </EntityItem>
                </Grid>
              ))}
            </EntityGrid>
          ) : (
            <NoEntitiesMessage>
              <Typography variant="body1" sx={{ 
                mb: 2, 
                color: 'var(--sevv-text-secondary, #757575)', 
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}>
                No entities found. Create your first entity or load sample data.
              </Typography>
              <ActionButton
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenEntityDialog()}
              >
                Add Entity
              </ActionButton>
            </NoEntitiesMessage>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <CardActionArea>
            <ActionButton
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCategoryDialog()}
            >
              Add New Category
            </ActionButton>
          </CardActionArea>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress color="secondary" />
            </Box>
          ) : categories.length > 0 ? (
            <StyledTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Category Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Order</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>{category.description}</TableCell>
                      <TableCell>{category.order}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenCategoryDialog(category)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteDialog(category.id, 'category')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          ) : (
            <NoEntitiesMessage>
              <Typography variant="body1" sx={{ 
                mb: 2, 
                color: 'var(--sevv-text-secondary, #757575)', 
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}>
                No categories found. Create your first category or load sample data.
              </Typography>
              <ActionButton
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenCategoryDialog()}
              >
                Add Category
              </ActionButton>
            </NoEntitiesMessage>
          )}
        </TabPanel>
      </StyledCard>

      {/* Category Dialog */}
      <StyledDialog open={categoryDialogOpen} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Category' : 'Add New Category'}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialogs}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'var(--sevv-text-secondary, #757575)',
              '&:hover': {
                color: 'var(--sevv-primary, #f97415)',
                backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <StyledForm onSubmit={handleSaveCategory}>
            <TextField
              name="name"
              label="Category Name"
              value={categoryForm.name}
              onChange={handleCategoryFormChange}
              fullWidth
              required
            />
            <TextField
              name="description"
              label="Description"
              value={categoryForm.description}
              onChange={handleCategoryFormChange}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              name="icon"
              label="Icon Name"
              value={categoryForm.icon}
              onChange={handleCategoryFormChange}
              fullWidth
              helperText="Material Icon name (e.g. 'category', 'chair', 'spa')"
            />
            <TextField
              name="order"
              label="Display Order"
              type="number"
              value={categoryForm.order}
              onChange={handleCategoryFormChange}
              fullWidth
            />
            <DialogActions>
              <Button onClick={handleCloseDialogs}>Cancel</Button>
              <ActionButton
                variant="contained"
                color="primary"
                type="submit"
                startIcon={<SaveIcon />}
              >
                Save
              </ActionButton>
            </DialogActions>
          </StyledForm>
        </DialogContent>
      </StyledDialog>

      {/* Entity Dialog */}
      <StyledDialog open={entityDialogOpen} onClose={handleCloseDialogs} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Entity' : 'Add New Entity'}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialogs}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'var(--sevv-text-secondary, #757575)',
              '&:hover': {
                color: 'var(--sevv-primary, #f97415)',
                backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <StyledForm onSubmit={handleSaveEntity}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="name"
                  label="Entity Name"
                  value={entityForm.name}
                  onChange={handleEntityFormChange}
                  fullWidth
                  required
                />
                <TextField
                  name="description"
                  label="Description"
                  value={entityForm.description}
                  onChange={handleEntityFormChange}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ mt: 2 }}
                />

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="category-select-label">Category</InputLabel>
                  <Select
                    labelId="category-select-label"
                    name="categoryId"
                    value={entityForm.categoryId}
                    onChange={handleEntitySelectChange}
                    required
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  name="imageUrl"
                  label="Image URL"
                  value={entityForm.imageUrl}
                  onChange={handleEntityFormChange}
                  fullWidth
                />
                <TextField
                  name="previewImageUrl"
                  label="Preview Image URL (optional)"
                  value={entityForm.previewImageUrl}
                  onChange={handleEntityFormChange}
                  fullWidth
                  sx={{ mt: 2 }}
                  helperText="If empty, the main image will be used"
                />

                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        name="width"
                        label="Width (tiles)"
                        type="number"
                        value={entityForm.width}
                        onChange={handleEntityFormChange}
                        fullWidth
                        InputProps={{ inputProps: { min: 1, max: 10 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        name="height"
                        label="Height (tiles)"
                        type="number"
                        value={entityForm.height}
                        onChange={handleEntityFormChange}
                        fullWidth
                        InputProps={{ inputProps: { min: 1, max: 10 } }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={entityForm.collisionEnabled}
                        onChange={handleEntitySwitchChange}
                        name="collisionEnabled"
                        color="secondary"
                      />
                    }
                    label="Enable Collision"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={entityForm.interactionEnabled}
                        onChange={handleEntitySwitchChange}
                        name="interactionEnabled"
                        color="secondary"
                      />
                    }
                    label="Enable Interaction"
                  />
                </Box>
              </Grid>
            </Grid>

            <DialogActions sx={{ mt: 2 }}>
              <Button onClick={handleCloseDialogs}>Cancel</Button>
              <ActionButton
                variant="contained"
                color="primary"
                type="submit"
                startIcon={<SaveIcon />}
              >
                Save
              </ActionButton>
            </DialogActions>
          </StyledForm>
        </DialogContent>
      </StyledDialog>

      {/* Delete Confirmation Dialog */}
      <StyledDialog open={confirmDeleteDialogOpen} onClose={handleCloseDialogs}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {itemToDelete?.type}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <ActionButton
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            startIcon={<DeleteIcon />}
          >
            Delete
          </ActionButton>
        </DialogActions>
      </StyledDialog>
    </Container>
  )
}

export default EntityCatalogueAdminPanel
